import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="reset-password-page">
      <Card className="w-full max-w-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Sparkles className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-extrabold">OutlierPulse</span>
        </div>

        {!token ? (
          <div className="text-center py-4" data-testid="reset-invalid-link">
            <h2 className="font-display text-xl font-bold">Invalid reset link</h2>
            <p className="text-sm text-muted-foreground mt-2">This link is missing or malformed. Please request a new one.</p>
            <Link to="/forgot-password" className="mt-6 inline-block text-sm text-primary hover:underline font-medium" data-testid="request-new-link">
              Request a new link
            </Link>
          </div>
        ) : (
          <>
            <h2 className="font-display text-2xl font-bold">Set a new password</h2>
            <p className="text-sm text-muted-foreground mt-1">Choose a new password for your account.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-button">
                {loading ? "Resetting..." : "Reset password"}
              </Button>
            </form>
            <div className="mt-4 text-sm text-muted-foreground text-center">
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="back-to-login-link">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
